import i18next from 'i18next';
import deepmerge from 'deepmerge';
import globalsService from './GlobalsService';
import environmentNames from '../environmentNames';

function deDuplicate(array) {
    return array.reduce((accumulator, outerItem) => {
        if (!accumulator.some(item => item === outerItem)) {
            accumulator.push(outerItem);
        }
        return accumulator;
    }, []);
}

const i18nService = {
    fallbackLanguage: 'en-US',

    // TODO This is getting messy real fast, we should do a complete refactoring of language identifiers across all of IA

    languageMapInsperaToIetf: {
        da_dk: 'da-DK',
        de_de: 'de-DE',
        en_us: 'en-US',
        es_co: 'es-CO',
        fi_fi: 'fi-FI',
        fr_fr: 'fr-FR',
        iw_il: 'he-IL',
        he_il: 'he-IL',
        is_is: 'is-IS',
        nl_nl: 'nl-NL',
        no_no: 'nb-NO',
        no_ny: 'nn-NO',
        no_no_ny: 'nn-NO',
        pl_pl: 'pl-PL',
        ro_ro: 'ro-RO',
        ru_ru: 'ru-RU',
        sv_se: 'sv-SE',
        cy_gb: 'cy_GB',
    },

    languageMapIetfToInsperaContent: {
        'da-DK': 'da_dk',
        'de-DE': 'de_de',
        'en-US': 'en_us',
        'es-CO': 'es_co',
        'fi-FI': 'fi_fi',
        'fr-FR': 'fr_fr',
        'he-IL': 'iw_il',
        'is-IS': 'is_is',
        'nl-NL': 'nl_nl',
        'nb-NO': 'no_no',
        'nn-NO': 'no_no_ny',
        'pl-PL': 'pl_pl',
        'ro-RO': 'ro_ro',
        'ru-RU': 'ru_ru',
        'sv-SE': 'sv_se',
        'cy_GB': 'cy_gb',
    },

    languageMapIetfToCKEditor: {
        'da-DK': 'da',
        'de-DE': 'de',
        'cy_GB': 'cy',
        'en-US': 'en',
        'es-CO': 'es',
        'fi-FI': 'fi',
        'fr-FR': 'fr',
        'he-IL': 'he',
        'is-IS': 'is',
        'nl-NL': 'nl',
        'nb-NO': 'nb',
        'nn-NO': 'nb',
        'pl-PL': 'pl',
        'ru-RU': 'ru',
        'sv-SE': 'sv',
    },

    languageMapIetfToWebSpellChecker: {
        'da-DK': 'da_DK',
        'de-DE': 'de_DE',
        'en-US': 'en_US',
        'es-CO': 'es_CO',
        'fi-Fi': 'fi_FI',
        'fr-FR': 'fr_FR',
        'he-IL': 'he_IL',
        'is-IS': 'is_IS',
        'nl-NL': 'nl_NL',
        'nb-NO': 'nb_NO',
        'nn-NO': 'nn_NO',
        'pl-PL': 'pl_PL',
        'ro-RO': 'ro_RO',
        'ru-RU': 'ru_RU',
        'sv-SE': 'sv_SE',
        'cy-GB': 'cy_GB',
    },

    languageMapIdToIetf: {
        1: 'nb-NO',
        2: 'en-US',
        3: 'nn-NO',
        4: 'fr-FR',
        5: 'ru-RU',
        6: 'de-DE',
        7: 'sv-SE',
        8: 'da-DK',
        9: 'is-IS',
        10: 'fi-FI',
        11: 'es-CO', // Should be es-ES, but mapped here since we only support Colombian Spanish in UI.
        12: 'pl-PL',
        13: 'es-CO',
        14: 'nl-NL',
        15: 'he-IL',
        16: 'ro-RO',
        17: 'cy-GB',
    },

    supportedLanguages: {
        'da-DK': 'Dansk',
        'es-CO': 'Español de América Latina',
        'en-US': 'English',
        'fi-FI': 'Finish',
        'fr-FR': 'Français',
        'he-IL': 'עִברִית',
        'nl-NL': 'Nederlands',
        'nb-NO': 'Norsk - bokmål',
        'nn-NO': 'Norsk - nynorsk',
        'pl-PL': 'Polski',
        'ro-RO': 'Română',
        'sv-SE': 'Svenska',
        'de-DE': 'Deutsch',
        'cy-GB': 'Welsh',
        'is-IS': 'Icelandic',
    },

    RTLDirectionLanguages: ['he-IL'],

    mappingForUnsupportedUILanguages: {
        'da-DK': 'en-US',
    },

    marketplaceLanguageOverrides: {
        fjord: {
            'en-US': 'en-GB-fjord',
        },
        ceq: {
            'en-US': 'en-GB-ceq',
        },
    },

    getContentLanguageCode(ietfLanguageCode) {
        return this.languageMapIetfToInsperaContent[ietfLanguageCode];
    },

    getJavaLocaleFromLanguageId(languageId) {
        const ietfLocale = this.languageMapIdToIetf[languageId];
        return this.languageMapIetfToInsperaContent[ietfLocale];
    },

    isRomanianLanguageOverrideEnabled() {
        return window.marketplaceProperties.UI_LANGUAGE_OVERRIDE === 'romanian';
    },

    getAvailableLanguages() {
        if (this.availableLanguages === undefined) {
            const contentAndUiLanguages = globalsService
                .getSupportedLanguages()
                .concat(globalsService.getSupportedContentLanguages());

            if (this.isRomanianLanguageOverrideEnabled()) {
                contentAndUiLanguages.push('ro_ro');
            }

            this.availableLanguages = deDuplicate(contentAndUiLanguages)
                .filter(language => this.languageMapInsperaToIetf[language] !== undefined);
        }

        return this.availableLanguages;
    },

    returnTranslationsObject(defaultLangCode, overrideLangCode) {
        const captions = require('../../i18n/index');
        if (
            ['en-GB-fjord', 'en-GB-ceq'].includes(overrideLangCode) &&
            captions[overrideLangCode]
        ) {
            const overrideCaptions = captions[overrideLangCode].translation;
            const captionsToOverride = captions[defaultLangCode].translation;

            captions[defaultLangCode].translation = deepmerge(captionsToOverride, overrideCaptions);
            delete captions[overrideLangCode];
            return captions;
        }

        return captions;
    },

    addRubricsExtraCaptions(captions) {
        const dynamicRubrics = window.dynamicRubrics;
        const isObject = dynamicRubrics !== null && (typeof dynamicRubrics === 'object') && !Array.isArray(dynamicRubrics);

        if (isObject && Object.keys(dynamicRubrics).length > 0) {
            try {
                return deepmerge(captions, dynamicRubrics);
            } catch (error) {
                return captions;
            }
        }

        return captions;
    },

    getOverrideLanguageCode(ietfLanguageCode) {
        const { UI_LANGUAGE_OVERRIDE } = window.marketplaceProperties;
        if (UI_LANGUAGE_OVERRIDE && this.marketplaceLanguageOverrides[UI_LANGUAGE_OVERRIDE]) {
            return this.marketplaceLanguageOverrides[UI_LANGUAGE_OVERRIDE][ietfLanguageCode];
        }
        return undefined;
    },

    buildResourceBundle(ietfLanguageCode) {
        if (process.env.NODE_ENV === environmentNames.UNIT_TESTING) {
            const translation = require('../../docs/i18n/dev/translation');
            return translation.default;
        }

        // if you have done "gulp captions", the languages will be bundled
        const overrideLanguageCode = this.getOverrideLanguageCode(ietfLanguageCode);
        const captions = this.returnTranslationsObject(ietfLanguageCode, overrideLanguageCode);
        return this.addRubricsExtraCaptions(captions);
    },

    getIetfLanguageCode(javaLocale, selectedLanguageId) {
        if (this.languageMapInsperaToIetf[javaLocale]) {
            return this.languageMapInsperaToIetf[javaLocale];
        }

        const languageId = parseInt(selectedLanguageId, 10);
        if (this.languageMapIdToIetf[languageId]) {
            return this.languageMapIdToIetf[languageId];
        }

        return this.fallbackLanguage;
    },

    initI18n(ietfLanguageCode) {
        const resources = this.buildResourceBundle(ietfLanguageCode);

        i18next.init({
            lng: this.mappingForUnsupportedUILanguages[ietfLanguageCode] || ietfLanguageCode,
            resources,
            interpolation: { prefix: '__', suffix: '__' },
            fallbackLng: [this.fallbackLanguage, 'dev'],
        });
    },

    /**
     * Changes the language used for translations.
     *
     * @param {string} language IETF language code, eg: en-US or nn-NO
     * @param {function} callback
     */
    changeLanguage(language, callback) {
        i18next.changeLanguage(
            this.mappingForUnsupportedUILanguages[language] || language,
            callback
        );
    },

    translate(key, options) {
        return i18next.t(key, options);
    },

    exists(key, options) {
        return i18next.exists(key, options);
    },
};

export default i18nService;

export function t(key, options) {
    return i18nService.translate(key, options);
}

export function setDocumentLanguage(language) {
    document.querySelector('html').setAttribute('lang', language);
}

export function getInteractionInputAriaLabelText(questionNumber) {
    return t('interactions.ariaLabelText') + (questionNumber !== 'undefined' ? questionNumber : '');
}
